import React, { useState } from 'react';
import { ChevronLeft, Tag, Search } from 'lucide-react';

const CATEGORY_COLORS = {
  Produce: '#3ab86c',
  Dairy: '#4a9eff',
  Meat: '#E8471E',
  Bakery: '#C8941A',
  Beverages: '#7c5cbf',
  Frozen: '#00bcd4',
  Pantry: '#ff9800',
  Household: '#9e9e9e',
  'Personal Care': '#e91e63',
  Other: '#607d8b',
};

const ItemsList = ({ store, items, onBack }) => {
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');

  const categories = ['all', ...new Set(items.map(i => i.category || 'Other'))];

  const filtered = items.filter(item => {
    const matchSearch = !search ||
      item.item_name.toLowerCase().includes(search.toLowerCase());
    const matchCat = categoryFilter === 'all' ||
      (item.category || 'Other') === categoryFilter;
    return matchSearch && matchCat;
  });

  const groupedByCategory = filtered.reduce((acc, item) => {
    const cat = item.category || 'Other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(item);
    return acc;
  }, {});

  return (
    <div className="items-list-page">
      <div className="summary-nav">
        <button className="back-btn" onClick={onBack}>
          <ChevronLeft size={16} /> Back to stores
        </button>
        <div className="summary-nav-title">
          <h2>{store.store_name}</h2>
          <p className="summary-nav-sub">
            {items.length} items · Total: <strong>${store.total.toFixed(2)}</strong>
          </p>
        </div>
      </div>

      {/* Search and filter */}
      <div className="items-controls">
        <div className="items-search">
          <Search size={14} />
          <input type="text" placeholder="Search items..."
            value={search} onChange={e => setSearch(e.target.value)}
            className="items-search-input"
          />
        </div>
        <div className="items-category-filters">
          {categories.map(cat => (
            <button key={cat}
              className={`category-filter-btn ${categoryFilter === cat ? 'category-filter-active' : ''}`}
              onClick={() => setCategoryFilter(cat)}
              style={categoryFilter === cat && cat !== 'all' ? {
                background: `${CATEGORY_COLORS[cat]}20`,
                borderColor: CATEGORY_COLORS[cat],
                color: CATEGORY_COLORS[cat]
              } : {}}>
              {cat === 'all' ? 'All' : cat}
            </button>
          ))}
        </div>
      </div>

      {/* Items grouped by category */}
      {Object.keys(groupedByCategory).length === 0 ? (
        <div className="summary-empty">
          <p>No items match your search.</p>
        </div>
      ) : (
        <div className="items-groups">
          {Object.entries(groupedByCategory).map(([category, catItems]) => (
            <div key={category} className="items-group">
              <div className="items-group-header">
                <div className="items-group-dot"
                  style={{ background: CATEGORY_COLORS[category] || '#666' }} />
                <span className="items-group-name">{category}</span>
                <span className="items-group-count">
                  {catItems.length} items · ${catItems.reduce((s, i) =>
                    s + (parseFloat(i.total_price) || 0), 0).toFixed(2)}
                </span>
              </div>
              <div className="items-group-list">
                {catItems.map((item, idx) => (
                  <div key={idx} className="item-row">
                    <div className="item-row-left">
                      <Tag size={12}
                        color={CATEGORY_COLORS[category] || '#666'} />
                      <span className="item-name">{item.item_name}</span>
                    </div>
                    <div className="item-row-right">
                      {item.quantity !== 1 && (
                        <span className="item-qty">
                          {item.quantity} × ${parseFloat(item.unit_price).toFixed(2)}
                        </span>
                      )}
                      <span className="item-price">
                        ${parseFloat(item.total_price).toFixed(2)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Total summary */}
      <div className="items-total-bar">
        <div className="items-total-row">
          <span>Subtotal</span>
          <span>${store.subtotal?.toFixed(2) || '—'}</span>
        </div>
        {store.tax > 0 && (
          <div className="items-total-row">
            <span>Tax</span>
            <span>${store.tax.toFixed(2)}</span>
          </div>
        )}
        <div className="items-total-row items-total-final">
          <span>Total</span>
          <span>${store.total.toFixed(2)}</span>
        </div>
      </div>
    </div>
  );
};

export default ItemsList;
